// web/src/tutorial/detail/fret-dance.ts
// FretDance · 软件界面详细说明（按页签逐屏、逐个参数）
//
// 写作原则（作者要求）：面向用户，不写用户看不到的实现细节；
// 参数名 / 按钮名逐字对齐界面原文，取值范围与默认值全部来自源码。
//
// 依据：G:\fretDance_rust
//   src/ui/app.rs（四个页签与顺序）、parameter_setting.rs、avatar.rs、avatar_display.rs、
//   midi_info_scan.rs、execute_operation.rs、execute_logic.rs、show_console.rs、
//   hand_recorder_editor.rs、fret_timeline.rs、components.rs、data.rs（默认值）、
//   src/midi/midi_to_note.rs（量化选项、降低八度与变调夹的算法）、
//   src/fret_dancer.rs（输出文件名与报告内容）、
//   asset/i18n/zh-CN.json 与 asset/i18n/en-US.json（界面原文，逐字对齐）

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const fretDanceDetail: TutorialDoc = {
  key: "fret_dance",
  slug: "fret-dance",
  kind: "detail",
  icon: "🎸",
  title: l("FretDance 软件界面详细说明", "FretDance UI Reference"),
  subtitle: l(
    "这页会按截图逐屏说明：角色设置、MIDI 选择、生成动画、右手编辑器四个页签里，每个可调参数控制什么、默认值大致是多少，以及什么场景下需要调整。",
    "A screen-by-screen reference for the four tabs — 角色设置 / Character Settings, MIDI选择 / MIDI Select, 生成动画 / Generate and 右手编辑器 / Recorder Editor — covering every adjustable parameter: what it controls, its range, its default and when to touch it.",
  ),
  readMinutes: 14,
  video: {
    hidden: true,
    caption: l("本页不内嵌视频", "No embedded video on this page"),
  },
  images: [
    {
      src: "/assets/image/products/fret-dance/CN/角色设置.png",
      srcEn: "/assets/image/products/fret-dance/EN/Character Parameters.png",
      label: l("角色设置", "Character Settings"),
      width: 1102,
      height: 932,
    },
    {
      src: "/assets/image/products/fret-dance/CN/MIDI选择.png",
      srcEn: "/assets/image/products/fret-dance/EN/MIDI Select.png",
      label: l("MIDI选择", "MIDI Select"),
      width: 1102,
      height: 932,
    },
    {
      src: "/assets/image/products/fret-dance/CN/指法时间轴.png",
      srcEn: "/assets/image/products/fret-dance/EN/Fingering timeline.png",
      label: l("指法时间轴", "Fingering Timeline"),
      width: 1102,
      height: 932,
    },
    {
      src: "/assets/image/products/fret-dance/CN/生成动画.png",
      srcEn: "/assets/image/products/fret-dance/EN/Generate Animation.png",
      label: l("生成动画", "Generate"),
      width: 1102,
      height: 932,
    },
    {
      src: "/assets/image/products/fret-dance/CN/右手Recorder编辑器.png",
      srcEn:
        "/assets/image/products/fret-dance/EN/Right-Hand Recorder Editor.png",
      label: l("右手编辑器", "Recorder Editor"),
      width: 1102,
      height: 932,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "how-to-read",
      title: l("这篇怎么用", "How to use this page"),
      blocks: [
        {
          kind: "p",
          text: l(
            "本页是**参数手册**：按软件页签的顺序，一屏一屏地拆开讲。右下角悬浮窗会跟着你滚到哪一节，自动切到那一屏的截图；正文里说「这一屏」的时候，看图就行。",
            "This page is a **parameter reference**: it walks the app tab by tab. The floating window in the bottom-right corner follows the section you are reading and switches to that screen; when the text says “this screen”, just look at the picture.",
          ),
        },
        {
          kind: "p",
          text: l(
            "只想先跑通一次完整流程（选演奏者 → 选 MIDI → 生成动画），看[快速上手](/quickstart/fret-dance.html)。本页适合已经跑过一遍、现在要对付某个具体问题的时候翻。",
            "To get a first run done — pick a performer, pick a MIDI file, generate — read the [Quick Start](/quickstart/fret-dance.html). This page is for when you have already run it once and now need to fix something specific.",
          ),
        },
        {
          kind: "note",
          text: l(
            "下面所有「范围」和「默认」都来自程序本身。「默认」指的是**全新安装、什么都没改过**时的取值；如果你动过某个滑块，看到的数字会不一样。",
            "Every range and default below comes from the program itself. “Default” means a **fresh install with nothing changed**; if you have moved a slider, you will see a different number.",
          ),
        },
        {
          kind: "ul",
          items: [
            l(
              "角色设置 —— 谁来弹、琴怎么调、手怎么动。",
              "角色设置 / Character Settings — who plays, how the guitar is tuned, how the hands move.",
            ),
            l(
              "MIDI选择 —— 弹哪首曲子、哪些轨道要生成动画。",
              "MIDI选择 / MIDI Select — which song, and which tracks get animated.",
            ),
            l(
              "生成动画 —— 按下按钮跑，并在控制台看结果。",
              "生成动画 / Generate — press the buttons and watch the console.",
            ),
            l(
              "右手编辑器 —— 检查左手按弦时间轴，手工修电吉他右手摇把记录。",
              "右手编辑器 / Recorder Editor — inspect the left-hand fingering timeline and hand-edit electric-guitar whammy records.",
            ),
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-character",
      title: l("角色设置", "Character Settings"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "启动后默认打开的就是这一页。左栏是 角色参数 卡片（编号 01），管「谁来弹、琴怎么调、手怎么动」；右栏是 人物信息 卡片（编号 02），管演奏者列表本身。**左栏改的是这一次生成的结果，右栏改的是硬盘上的角色登记**，两者互不影响。",
            "This is the tab the app opens on. The left column holds the 角色参数 / Character Parameters card (numbered 01) — who plays, how the guitar is tuned, how the hands move. The right column holds 人物信息 / Character Info (numbered 02) — the performer list itself. **The left column changes this run's output; the right column changes the character registry on disk.** They do not affect each other.",
          ),
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "第一次用这一页，只有 Avatar: 需要动。其余全部保持默认，等你看到动画再回来按症状找参数。",
            "On your first visit only Avatar: needs touching. Leave the rest at its default and come back to it once you have seen the animation.",
          ),
        },
      ],
      subs: [
        {
          id: "character-basic",
          title: l(
            "01 角色参数：选人和帧率",
            "01 Character Parameters — performer and frame rate",
          ),
          image: { image: 0 },
          blocks: [
            {
              kind: "p",
              text: l(
                "卡片标题下面只有两行控件，但它们决定的东西比看上去多。",
                "Only two rows of controls sit under the card title, but they decide more than they look like.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("什么时候调", "When to change it"),
              ],
              rows: [
                [
                  l("Avatar:", "Avatar:"),
                  l(
                    "选择这次生成用哪个演奏者。下拉列表里的名字来自角色登记文件 asset/controller_infos/avatar_infos.json，程序自带若干指弹吉他、贝斯、电吉他角色。",
                    "Chooses which performer this run uses. The dropdown is filled from the character registry asset/controller_infos/avatar_infos.json, which ships with several fingerstyle, bass and electric-guitar characters.",
                  ),
                  l("登记文件里的全部角色", "Every character in the registry"),
                  l("Miku", "Miku"),
                  l(
                    "换角色时。选完看一眼右栏 乐器:，它决定右手怎么拨弦。",
                    "When switching performers. Check 乐器: / Instrument on the right afterwards — it decides how the right hand plucks.",
                  ),
                ],
                [
                  l("FPS:", "FPS:"),
                  l(
                    "动画帧率。所有动画数据都是按帧写出的，指法时间轴、右手记录里的帧号也都以它为准。",
                    "The animation frame rate. All animation data is written per frame, and the frame numbers in the fingering timeline and right-hand records follow it too.",
                  ),
                  l(
                    "整数，拖动数字框调整（步进 1）",
                    "An integer, dragged in steps of 1",
                  ),
                  l("60", "60"),
                  l(
                    "要和你在 Blender / Unreal 场景里用的帧率一致。生成之后再改它必须重跑，否则动画长度对不上。",
                    "It must match the frame rate of your Blender / Unreal scene. Changing it after generating means re-running, or the animation length will not line up.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "character-info",
          title: l(
            "02 人物信息：演奏者列表",
            "02 Character Info — the performer list",
          ),
          image: { image: 0 },
          blocks: [
            {
              kind: "p",
              text: l(
                "右栏显示当前角色的预览图、一行 乐器:，以及底部的三个管理按钮。这三个按钮改的是**登记表**：新角色会写回 asset/controller_infos/avatar_infos.json，以后每次启动都在下拉里。",
                "The right column shows the current character's preview image, an 乐器: / Instrument line and three management buttons. These edit the **registry**: a new character is written back to asset/controller_infos/avatar_infos.json and shows up in the dropdown on every later launch.",
              ),
            },
            {
              kind: "table",
              head: [
                l("按钮", "Button"),
                l("点了会发生什么", "What happens when you press it"),
              ],
              rows: [
                [
                  l("✏ 修改", "✏ 修改 / ✏ Edit"),
                  l(
                    "右栏整块换成 修改Avatar 编辑卡片，四个字段预填当前角色的值。",
                    "The whole right column becomes the 修改Avatar / Edit Avatar card, pre-filled with the current character's values.",
                  ),
                ],
                [
                  l("➕ 新增", "➕ 新增 / ➕ Add"),
                  l(
                    "右栏换成 新增Avatar 编辑卡片，字段清空、乐器回到 指弹吉他。",
                    "The right column becomes the 新增Avatar / New Avatar card with empty fields and the instrument back at 指弹吉他 / Fingerstyle Guitar.",
                  ),
                ],
                [
                  l("🗑 删除", "🗑 删除 / 🗑 Delete"),
                  l(
                    '弹出 确认删除 窗口，写明 确定要删除Avatar "{}" 吗？ 与 此操作不可撤销。，要再点 ✅ 确认删除 才真的删；❌ 取消 只是关掉窗口。只剩一个角色时删不掉，会提示 不能删除最后一个Avatar。',
                    'Opens the 确认删除 / Confirm Delete window asking 确定要删除Avatar "{}" 吗？ plus 此操作不可撤销。. Only ✅ 确认删除 / ✅ Confirm Delete actually deletes; ❌ 取消 / ❌ Cancel just closes it. With a single character left, deletion is refused with 不能删除最后一个Avatar.',
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "编辑卡片里一共四项：名字: 文本输入；图片: 文件名加一个 📁 浏览（只收 png / gif）；配置: 文件名加一个 📁 浏览（只收 json，也就是这个角色的 controller info）；乐器: 下拉三选一——指弹吉他 / 贝斯 / 电吉他。底部是 ❌ 取消 与 💾 保存。",
                "The edit card has four fields: 名字: / Name as text; 图片: / Image as a file name with a 📁 浏览 / Browse button (png / gif only); 配置: / Config as a file name with another 📁 浏览 (json only — this character's controller info); and the 乐器: / Instrument dropdown with three choices: 指弹吉他 / Fingerstyle Guitar, 贝斯 / Bass, 电吉他 / Electric Guitar. ❌ 取消 and 💾 保存 sit at the bottom.",
              ),
            },
            {
              kind: "note",
              text: l(
                "乐器: 这一行显示的是登记文件里的**内部乐器名**——finger_style_guitar、bass、electric_guitar，所以在中文界面上它也是英文的。中文名只出现在编辑对话框的下拉里。",
                "The 乐器: / Instrument line prints the **internal instrument name** stored in the registry — finger_style_guitar, bass or electric_guitar — so it reads in English even on the Chinese UI. The localized names only appear in the edit dialog's dropdown.",
              ),
            },
            {
              kind: "callout",
              tone: "tip",
              title: l(
                "换角色会顺手换调弦",
                "Switching performers also switches the tuning",
              ),
              text: l(
                "选中角色后，程序会按 乐器: 自动套用调弦预设：贝斯 → Bass调弦 (E A D G)，指弹 / 电吉他 → 标准调弦 (E A D G B E)。所以切角色时左栏弦数突然从 6 变 4 是正常的。另外 📁 浏览 选中的图片与配置会被复制进 asset/img/ 与 asset/controller_infos/。",
                "After you pick a character, the app applies a tuning preset based on 乐器: / Instrument: bass → Bass调弦 (E A D G) / Bass Tuning, fingerstyle and electric → 标准调弦 (E A D G B E) / Standard Tuning. A jump from six strings to four when switching performers is therefore normal. Files chosen with 📁 浏览 / Browse are copied into asset/img/ and asset/controller_infos/.",
              ),
            },
            {
              kind: "note",
              text: l(
                "怎么把自己的模型做成这里能选的角色（controller info 里要有哪些骨骼与坐标），见[自定义弹拨演奏者](/advanced/fret-dance.html)。",
                "To turn your own model into a character you can pick here — which bones and coordinates the controller info needs — see [Custom Plucked-String Performer](/advanced/fret-dance.html).",
              ),
            },
          ],
        },
        {
          id: "character-tuning",
          title: l("吉他弦音高与调弦预设", "String pitches and tuning presets"),
          image: { image: 0 },
          blocks: [
            {
              kind: "p",
              text: l(
                "吉他弦音高 (从最细的弦到最粗的弦): 这一段决定「琴上有几根弦、每根弦的空弦是什么音」。顺序是**从最细（音最高）到最粗（音最低）**，所以 第1弦 排在最上面。所有指法计算都建立在这组音高上——写错一个音，整首曲子的按位都会跟着偏。",
                "吉他弦音高 (从最细的弦到最粗的弦): / Guitar string pitches decides how many strings the instrument has and what each open string sounds. The order runs **from thinnest (highest) to thickest (lowest)**, so 第1弦 / String 1 is on top. Every fingering calculation is built on these pitches — get one wrong and the whole song's positions shift with it.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("什么时候调", "When to change it"),
              ],
              rows: [
                [
                  l("常用调弦:", "常用调弦: / Common tunings:"),
                  l(
                    "一键把整组弦音高换成某个预设。下拉里六项：标准调弦 (E A D G B E)、Drop D (D A D G B E)、科庸巴巴特殊调弦 (D A D G B F)、Open D (D A D F# A D)、Bass调弦 (E A D G)、七弦吉他 (B E A D G B E)。",
                    "Replaces the whole set of string pitches with a preset. The dropdown lists six: 标准调弦 (E A D G B E) / Standard Tuning, Drop D (D A D G B E), 科庸巴巴特殊调弦 (D A D G B F) / Koyunbaba Tuning, Open D (D A D F# A D), Bass调弦 (E A D G) / Bass Tuning and 七弦吉他 (B E A D G B E) / 7-String Guitar.",
                  ),
                  l("上面六个预设", "The six presets above"),
                  l(
                    "显示 选择预设调弦，不选中任何一项",
                    "Shows 选择预设调弦 / Select preset tuning, with nothing selected",
                  ),
                  l(
                    "特殊调弦的曲子。套完预设还能在下面的输入框里改个别弦。",
                    "Songs in a special tuning. After applying a preset you can still edit individual strings below.",
                  ),
                ],
                [
                  l(" ➕ 增加弦数", " ➕ 增加弦数 /  ➕ Add string"),
                  l(
                    "在列表最下面添一根空弦。",
                    "Appends one empty string at the bottom of the list.",
                  ),
                  l("每次 +1", "One at a time"),
                  l("—", "—"),
                  l(
                    "七弦吉他、五弦贝斯等多弦乐器。",
                    "Seven-string guitars, five-string basses and the like.",
                  ),
                ],
                [
                  l(" ➖ 减少弦数", " ➖ 减少弦数 /  ➖ Remove string"),
                  l("删掉最下面一根弦。", "Removes the bottom string."),
                  l(
                    "每次 −1，至少保留 1 根",
                    "One at a time, at least 1 string remains",
                  ),
                  l("—", "—"),
                  l(
                    "四弦贝斯、尤克里里之类。",
                    "Four-string bass, ukulele and similar.",
                  ),
                ],
                [
                  l("第1弦 … 第6弦", "第1弦 … 第6弦 / String 1 … String 6"),
                  l(
                    "每根弦的空弦音名，右边括号里实时显示它对应的 MIDI 音符号。小写字母表示高八度、大写表示低八度，末尾数字再往下压若干八度。",
                    "The open-string pitch name, with its MIDI note number shown live in brackets. Lower case means an octave up, upper case means the base octave, and a trailing digit drops further octaves.",
                  ),
                  l("文本，如 e b G D A E1", "Text such as e, b, G, D, A, E1"),
                  l(
                    "标准吉他六弦 e b G D A E1（MIDI 64 / 59 / 55 / 50 / 45 / 40）",
                    "Standard six-string guitar: e b G D A E1 (MIDI 64 / 59 / 55 / 50 / 45 / 40)",
                  ),
                  l(
                    "曲子用的不是预设里的调弦；或者刚换完角色，想微调个别弦。",
                    "When the song is not in a preset tuning, or right after switching performers and wanting to nudge one string.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "音名写不对，按法就算不出来",
                "A bad pitch name breaks the fingering",
              ),
              text: l(
                "音名写错时右边括号里会给出一个明显不对的数字（解析不出来时回落到 0），按法也就跟着全错，生成的动画会大范围跑偏。核对每根弦右边的 MIDI 数字最稳：标准吉他从上到下应该是 64 / 59 / 55 / 50 / 45 / 40。",
                "With a bad pitch name the bracket shows an obviously wrong number (it falls back to 0 when the name cannot be parsed), and the fingering goes wrong with it — the animation drifts out of shape. The reliable check is the MIDI number to the right of each string: for a standard guitar it must read 64 / 59 / 55 / 50 / 45 / 40 from top to bottom.",
              ),
            },
          ],
        },
        {
          id: "character-options",
          title: l("四个开关与变调夹", "Four checkboxes and the capo"),
          image: { image: 0 },
          blocks: [
            {
              kind: "p",
              text: l(
                "弦音高下面是一列勾选框和一个 变调夹位置: 数字。它们的共同点是：**改了之后整首曲子的按法都会变**，不是微调。",
                "Under the pitches sit a column of checkboxes and a 变调夹位置: / Capo position number. All of them share one property: **they change how the entire song is fingered**, so they are not fine-tuning.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("什么时候调", "When to change it"),
              ],
              rows: [
                [
                  l("降低八度", "降低八度 / Octave down"),
                  l(
                    "把 MIDI 里读到的每个音整体降一个八度再算按法（音高数字减 12）。",
                    "Shifts every note read from the MIDI down one octave before computing positions (the note number minus 12).",
                  ),
                  l("勾选 / 不勾", "Ticked / unticked"),
                  l("不勾", "Unticked"),
                  l(
                    "MIDI 是按实际音高写的，和吉他实际发声差一个八度时。判断方法：生成的按位整体偏高一个八度，就往这里看。",
                    "When the MIDI is written at concert pitch but the guitar sounds an octave lower. Symptom: every position comes out an octave too high.",
                  ),
                ],
                [
                  l("变调夹位置:", "变调夹位置: / Capo position:"),
                  l(
                    "告诉程序变调夹夹在第几品：同一个谱面音对应的按位会整体往低把位挪相应品数，手更好按。",
                    "Tells the app which fret the capo sits on: the same written pitch maps to a position that many frets lower, which is easier to play.",
                  ),
                  l(
                    "整数 0 – 12（拖动调整）",
                    "An integer from 0 to 12, dragged",
                  ),
                  l("0（不夹）", "0 (no capo)"),
                  l(
                    "曲子是夹着变调夹弹的。夹得越高，生成的按位越低、越靠近琴枕。",
                    "When the song is played with a capo. The higher the capo, the lower and closer to the nut the positions become.",
                  ),
                ],
                [
                  l("使用泛音", "使用泛音 / Use harmonics"),
                  l(
                    "允许在按法搜索里使用泛音音色。",
                    "Lets the fingering search use harmonic notes.",
                  ),
                  l("勾选 / 不勾", "Ticked / unticked"),
                  l("不勾", "Unticked"),
                  l(
                    "曲子里有泛音段落，想让手指按在泛音点上而不是普通品位。",
                    "When the song has harmonics and you want fingers on the harmonic nodes rather than ordinary frets.",
                  ),
                ],
                [
                  l("禁用横按", "禁用横按 / Disable barre chords"),
                  l(
                    "禁止使用横按指法：即便某个和弦最省事的按法是横按，生成动画时也不会把它当成横按处理。",
                    "Forbids barre fingering: even where a barre would be the cheapest option, the animation will not treat it as one.",
                  ),
                  l("勾选 / 不勾", "Ticked / unticked"),
                  l("不勾", "Unticked"),
                  l(
                    "生成出来的横按太别扭，或者你的角色手型压不住横按。代价是按位可能变得更多、换把更频繁。",
                    "When the generated barres look awkward or your character's hand cannot hold one. The cost is more positions and more position shifts.",
                  ),
                ],
                [
                  l(
                    "应用勾弦逻辑 (Pull-off)",
                    "应用勾弦逻辑 (Pull-off) / Apply pull-off logic",
                  ),
                  l(
                    "在左手记录里找出可以用勾弦（Pull-off）连起来的音，让它们不再各拨一次。",
                    "Finds notes in the left-hand records that a pull-off can connect, so they no longer need a separate pluck.",
                  ),
                  l("勾选 / 不勾", "Ticked / unticked"),
                  l("不勾", "Unticked"),
                  l(
                    "想要更接近真人的连音、右手动作少一点时打开；反过来，出现「该响的弦没响」时，先把它关掉再对比一次。",
                    "Turn it on for more human-sounding legato and less right-hand work. If a string that should ring stays silent, switch it off and compare.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "character-timeline",
          title: l(
            "动画时间线设置：手的节奏",
            "Animation timeline settings — hand timing",
          ),
          image: { image: 0 },
          blocks: [
            {
              kind: "p",
              text: l(
                "动画时间线设置: 下的三个滑块控制「动作有多快」，单位都是秒。**它们是纯节奏参数**：不改按哪个品，只改手什么时候到、什么时候走。觉得动画「僵硬」或者「赶不上」，先来这里。三个滑块的取值范围完全一样。",
                "The three sliders under 动画时间线设置: / Animation timeline settings control how fast motions happen, all in seconds. **They are pure timing parameters**: they never change which fret is pressed, only when the hand arrives and leaves. If the animation looks stiff or is always behind, start here. All three share the same range.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("什么时候调", "When to change it"),
              ],
              rows: [
                [
                  l("按弦时间:", "按弦时间: / Press time:"),
                  l(
                    "手指从准备到按下去的时间间隔。界面提示是 按弦动作的时间间隔。",
                    "The interval between getting ready and pressing down. The tooltip reads 按弦动作的时间间隔.",
                  ),
                  l(
                    "0.01 – 1.00 秒（步进 0.001）",
                    "0.01 – 1.00 s (steps of 0.001)",
                  ),
                  l("0.100 秒", "0.100 s"),
                  l(
                    "快曲子显得手指来不及落下去时调小；想让动作更从容、更有重量感时调大。",
                    "Lower it when fast passages look like the fingers cannot land in time; raise it for a more deliberate, weightier feel.",
                  ),
                ],
                [
                  l("抬指时间:", "抬指时间: / Release time:"),
                  l(
                    "手指从保持到抬起的时间间隔。界面提示是 抬指动作的时间间隔。",
                    "The interval between holding and lifting off. The tooltip reads 抬指动作的时间间隔.",
                  ),
                  l(
                    "0.01 – 1.00 秒（步进 0.001）",
                    "0.01 – 1.00 s (steps of 0.001)",
                  ),
                  l("0.100 秒", "0.100 s"),
                  l(
                    "手指看着「黏」在弦上不走时调小；想看到明显的抬手动作时调大。",
                    "Lower it when fingers seem glued to the strings; raise it to make the lift visible.",
                  ),
                ],
                [
                  l("移动速度:", "移动速度: / Move speed:"),
                  l(
                    "换把位时移动一个把位所需的时间。界面提示是 移动一个把位所需时间。",
                    "How long it takes to move one fret position. The tooltip reads 移动一个把位所需时间.",
                  ),
                  l(
                    "0.01 – 1.00 秒（步进 0.001）",
                    "0.01 – 1.00 s (steps of 0.001)",
                  ),
                  l("0.100 秒", "0.100 s"),
                  l(
                    "大幅换把看起来像瞬移时调大；换把太拖沓、跟不上曲子时调小。",
                    "Raise it when long shifts look like teleporting; lower it when shifts drag behind the music.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "每个滑块右边都会显示当前值（中文界面是 0.100秒，英文界面是 0.100 s）。鼠标停在滑块上会弹出上面那句说明。",
                "Each slider prints its current value to the right (0.100秒 on the Chinese UI, 0.100 s on the English one). Hovering the slider shows the tooltip quoted above.",
              ),
            },
          ],
        },
        {
          id: "character-vibrato",
          title: l("揉弦设置：两个倍率", "Vibrato settings — two multipliers"),
          image: { image: 0 },
          blocks: [
            {
              kind: "p",
              text: l(
                "揉弦设置: 在 角色参数 卡片最底部，是两个倍率滑块。1.00 就是默认手感，往两边调分别是「更慢更大」和「更快更小」。",
                "揉弦设置: / Vibrato settings sit at the very bottom of the 角色参数 / Character Parameters card and hold two multiplier sliders. 1.00 is the default feel; moving either way gives you slower-and-wider or faster-and-tighter.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("什么时候调", "When to change it"),
              ],
              rows: [
                [
                  l("揉弦速度:", "揉弦速度: / Vibrato speed:"),
                  l(
                    "揉弦快慢的倍率，越大周期越短（界面提示：实际周期 = 0.3 / 速度 秒）。",
                    "The vibrato speed multiplier; higher means a shorter cycle (tooltip: actual cycle = 0.3 / speed seconds).",
                  ),
                  l(
                    "0.50 – 2.00 倍（步进 0.01）",
                    "0.50 – 2.00x (steps of 0.01)",
                  ),
                  l("1.00 倍", "1.00x"),
                  l(
                    "揉弦抖得太快像颤音、或慢得几乎看不出来时。",
                    "When the wobble is so fast it reads as a tremolo, or so slow it is invisible.",
                  ),
                ],
                [
                  l("揉弦幅度:", "揉弦幅度: / Vibrato amplitude:"),
                  l(
                    "揉弦幅度倍率。界面提示明确写了限制：只对**指弹 / 贝斯的平移揉弦**生效，电吉他的旋转幅度固定 5°。",
                    "The vibrato amplitude multiplier. Its tooltip states the limit plainly: it applies to **fingerstyle / bass translation vibrato** only — electric-guitar rotation amplitude is fixed at 5°.",
                  ),
                  l(
                    "0.50 – 2.00 倍（步进 0.01）",
                    "0.50 – 2.00x (steps of 0.01)",
                  ),
                  l("1.00 倍", "1.00x"),
                  l(
                    "指弹 / 贝斯揉弦幅度太夸张或几乎看不到时。用电吉他时这一项没有效果。",
                    "When fingerstyle or bass vibrato is too wide or barely visible. It has no effect on an electric guitar.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "这一组滑块在截图的可视区域之外（需要把 角色参数 卡片向下滚动才看得到），所以没有单独的截图；位置就在 移动速度: 的下面。",
                "These sliders fall outside the visible area of the screenshot (you have to scroll the 角色参数 / Character Parameters card), so there is no separate shot for them; they sit right below 移动速度: / Move speed:.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-midi",
      title: l("MIDI选择", "MIDI Select"),
      image: { image: 1 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一页解决两件事：**用哪个 MIDI 文件**，以及**文件里的哪些轨道要生成动画**。左栏从上到下是 MIDI文件管理（01）与 参数设置（02），右栏是 轨道选择（03）。",
            "This tab answers two questions: **which MIDI file**, and **which of its tracks get animated**. The left column holds MIDI文件管理 / MIDI File Management (01) and 参数设置 / Parameter Settings (02); the right column holds 轨道选择 / Track Selection (03).",
          ),
        },
        {
          kind: "note",
          text: l(
            "参数设置 里的 通道号:、FPS: 和 角色设置 页是同一份数据：在哪儿改都一样，两边会同时变。",
            "通道号: / Channel and FPS: in 参数设置 are the same values as on the 角色设置 tab: changing either place changes both.",
          ),
        },
      ],
      subs: [
        {
          id: "midi-file",
          title: l(
            "01 MIDI文件管理：把文件拿进来",
            "01 MIDI File Management — getting the file in",
          ),
          image: { image: 1 },
          blocks: [
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("什么时候用", "When to use it"),
              ],
              rows: [
                [
                  l("路径: {}", "路径: {} / Path: {}"),
                  l(
                    "显示当前 MIDI 的**文件名**（不是完整路径）。没有文件时显示 未选择MIDI文件。",
                    "Shows the current MIDI's **file name** (not the full path). With no file it reads 未选择MIDI文件 / No MIDI file selected.",
                  ),
                  l("—", "—"),
                  l(
                    "启动时预填 asset/midi/Sunburst.mid",
                    "On startup it is pre-filled with asset/midi/Sunburst.mid",
                  ),
                  l("核对用", "For checking"),
                ],
                [
                  l("📂 选择MIDI文件", "📂 选择MIDI文件 / 📂 Select MIDI File"),
                  l(
                    "打开文件对话框（过滤 .mid / .midi），选中的文件立刻成为当前 MIDI。",
                    "Opens a file dialog filtered to .mid / .midi; the file you pick becomes the current MIDI immediately.",
                  ),
                  l(".mid / .midi", ".mid / .midi"),
                  l("—", "—"),
                  l("换曲子时。", "Whenever you switch songs."),
                ],
                [
                  l("🔍 扫描MIDI信息", "🔍 扫描MIDI信息 / 🔍 Scan MIDI Info"),
                  l(
                    "读取文件里的轨道，把右栏 轨道选择 的表格填满。成功时下方出现横幅 MIDI轨道扫描完成。",
                    "Reads the file's tracks and fills the 轨道选择 table on the right. On success a banner reads MIDI轨道扫描完成 / MIDI track scan complete.",
                  ),
                  l("—", "—"),
                  l("—", "—"),
                  l(
                    "每次换完文件都要点一次，表格才会有内容。",
                    "Press it once after every file change, or the table stays empty.",
                  ),
                ],
                [
                  l(
                    "📥 将 MIDI 文件拖放到此处",
                    "📥 将 MIDI 文件拖放到此处 / 📥 Drop a MIDI file here",
                  ),
                  l(
                    "把文件从资源管理器拖进窗口，效果等同于 📂 选择MIDI文件。下方小字写明 支持 .mid 和 .midi 格式。",
                    "Drag a file in from Explorer; same effect as 📂 选择MIDI文件. The caption below reads 支持 .mid 和 .midi 格式 / Supports .mid and .midi formats.",
                  ),
                  l(".mid / .midi", ".mid / .midi"),
                  l("—", "—"),
                  l(
                    "懒得点按钮时。",
                    "When you cannot be bothered with the dialog.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "拖进来一个不是 MIDI 的文件，会在下方弹出红色横幅：扩展名不对是 错误：文件扩展名不正确，期望 .mid 或 .midi，实际为 .{}，完全没有扩展名是 错误：拖放的文件没有扩展名。文件不存在或读不出来时，扫描的横幅会带上具体原因（扫描MIDI轨道信息时出错: {}）。",
                "Dropping a non-MIDI file raises a red banner: a wrong extension gives Error: incorrect file extension; expected .mid or .midi, got .{}, and no extension at all gives Error: the dropped file has no extension. If the file is missing or unreadable, the scan banner carries the reason (Error scanning MIDI track info: {}).",
              ),
            },
          ],
        },
        {
          id: "midi-params",
          title: l(
            "02 参数设置：通道、帧率、量化、轨道",
            "02 Parameter Settings — channel, fps, quantization, tracks",
          ),
          image: { image: 1 },
          blocks: [
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("什么时候调", "When to change it"),
              ],
              rows: [
                [
                  l("通道号:", "通道号: / Channel:"),
                  l(
                    "只处理指定 MIDI 通道上的音符。-1 表示不按通道过滤，全部通道都读。",
                    "Processes only notes on the given MIDI channel. -1 means no channel filtering — everything is read.",
                  ),
                  l(
                    "整数（拖动调整，-1 表示不过滤）",
                    "An integer, dragged (-1 means no filtering)",
                  ),
                  l("-1", "-1"),
                  l(
                    "一个文件里塞了多个声部、只想取其中一路时。先扫描，看轨道表里 通道 列的实际通道号再填。",
                    "When one file holds several parts and you want just one. Scan first and read the 通道 / Channel column of the track table.",
                  ),
                ],
                [
                  l("FPS:", "FPS:"),
                  l(
                    "动画帧率，与 角色设置 页的 FPS: 是同一个值。",
                    "The animation frame rate — the same value as FPS: on the 角色设置 / Character Settings tab.",
                  ),
                  l("整数（步进 1）", "An integer, steps of 1"),
                  l("60", "60"),
                  l(
                    "要和目标软件的帧率一致；改完必须重新生成。",
                    "It must match your target app's frame rate; re-generate after changing it.",
                  ),
                ],
                [
                  l("音符量化:", "音符量化: / Note quantization:"),
                  l(
                    "把音符起点对齐到节拍网格，避免演奏里的微小提前/滞后被如实搬进动画。",
                    "Snaps note starts onto a rhythmic grid so tiny early/late playing is not copied into the animation verbatim.",
                  ),
                  l(
                    "无量化 / 1/32 / 1/64 / 1/128",
                    "无量化 / No quantization, 1/32, 1/64, 1/128",
                  ),
                  l("1/64", "1/64"),
                  l(
                    "节奏听着对不齐时选更细的一档；反过来，不想让程序挪动任何音符时选 无量化。",
                    "Pick a finer grid when the rhythm sounds misaligned; choose 无量化 / No quantization when you do not want notes moved at all.",
                  ),
                ],
                [
                  l("已选择的轨道:", "已选择的轨道: / Selected tracks:"),
                  l(
                    "以徽标形式列出已经加入生成的轨道（轨道 {}），只读。一个都没有时显示 无轨道。",
                    "Lists the tracks already added to the run as 轨道 {} / Track {} badges. Read-only; with none it reads 无轨道 / No tracks.",
                  ),
                  l("—", "—"),
                  l(
                    "启动时预填 轨道 1",
                    "Pre-filled with 轨道 1 / Track 1 on startup",
                  ),
                  l(
                    "要改它得去右栏勾选轨道再点 ✅ 添加选中的轨道。默认的 轨道 1 和你的 MIDI 无关，换文件后记得核对。",
                    "Change it by ticking tracks on the right and pressing ✅ 添加选中的轨道. The default 轨道 1 has nothing to do with your MIDI — check it after every file change.",
                  ),
                ],
                [
                  l(
                    "启用吉他音量偏移动画",
                    "启用吉他音量偏移动画 / Enable guitar volume offset animation",
                  ),
                  l(
                    "勾上后多出两件事：下面展开 音量偏移参数 一组控件，生成动画 页的 操作 里多出 7.生成吉他偏移动画。",
                    "Ticking it does two things: the 音量偏移参数 / Volume Offset Parameters group appears below, and 操作 on the 生成动画 tab gains 7.生成吉他偏移动画 / 7. Generate guitar offset animation.",
                  ),
                  l("勾选 / 不勾", "Ticked / unticked"),
                  l("不勾", "Unticked"),
                  l(
                    "想让整把吉他跟着音乐响度前后 / 左右晃动时。它要多分析一遍音频，耗时会明显变长。",
                    "When you want the whole guitar to sway with the music's loudness. It analyses the audio as well, so expect a noticeably longer run.",
                  ),
                ],
              ],
            },
          ],
        },
        {
          id: "midi-volume-offset",
          title: l(
            "音量偏移参数：让吉他跟着响度晃",
            "Volume Offset Parameters — sway with the loudness",
          ),
          image: { image: 1 },
          blocks: [
            {
              kind: "p",
              text: l(
                "只有勾上 启用吉他音量偏移动画 才会展开这一组。它的思路是：分析你给的一段音频，找出「响」的时刻，让整把吉他按这些时刻做位置与旋转上的偏移。",
                "This group only appears once 启用吉他音量偏移动画 is ticked. The idea: analyse an audio file you supply, find the loud moments, and offset the whole guitar's position and rotation at those moments.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it does"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("什么时候调", "When to change it"),
              ],
              rows: [
                [
                  l(
                    "📂 选择 MP3 文件",
                    "📂 选择 MP3 文件 / 📂 Select MP3 File",
                  ),
                  l(
                    "选用来分析的音频（过滤 mp3 / wav）。左侧一行会显示 MP3: {} 的形式——{} 处是所选文件名，没选时显示 未选择。",
                    "Chooses the audio to analyse (mp3 / wav). A line above shows MP3: {} with the chosen file name in place of {}, or 未选择 / Not selected when empty.",
                  ),
                  l("MP3 / WAV", "MP3 / WAV"),
                  l("空（未选择）", "Empty (none selected)"),
                  l(
                    "要用这个功能就必须选，否则第 7 步没有输入。",
                    "Required for this feature — step 7 has no input without it.",
                  ),
                ],
                [
                  l("最大位置偏移:", "最大位置偏移: / Max position offset:"),
                  l(
                    "整把吉他前后 / 左右平移的最大幅度，数字后面带 %。",
                    "The largest translation of the whole guitar, shown with a % suffix.",
                  ),
                  l("0 – 100", "0 – 100"),
                  l("10", "10"),
                  l(
                    "觉得吉他晃得太远就调小，几乎看不出来就调大。",
                    "Lower it when the guitar swings too far, raise it when the motion is invisible.",
                  ),
                ],
                [
                  l(
                    "最大旋转偏移(°):",
                    "最大旋转偏移(°): / Max rotation offset (°):",
                  ),
                  l(
                    "整把吉他旋转的最大角度，单位是度。",
                    "The largest rotation of the whole guitar, in degrees.",
                  ),
                  l("0 – 45", "0 – 45"),
                  l("10", "10"),
                  l(
                    "和位置偏移搭配着调：想更「躁」就两个一起加大。",
                    "Tune it together with the position offset: raise both for a wilder feel.",
                  ),
                ],
                [
                  l("检测灵敏度:", "检测灵敏度: / Detection sensitivity:"),
                  l(
                    "阈值倍数：设得越高，被算作「响」的时刻就越少。",
                    "A threshold multiplier: the higher it is, the fewer moments count as loud.",
                  ),
                  l("0.1 – 5.0（步进 0.05）", "0.1 – 5.0 (steps of 0.05)"),
                  l("1.5", "1.5"),
                  l(
                    "晃动太密集、太碎时调大；整首歌几乎不晃时调小。",
                    "Raise it when the motion is too dense and jittery, lower it when almost nothing moves.",
                  ),
                ],
                [
                  l("噪声过滤比例:", "噪声过滤比例: / Noise filter ratio:"),
                  l(
                    "从信号里扣掉的背景噪声比例，避免把底噪当成响度点。",
                    "The share of background noise subtracted from the signal so hiss is not mistaken for a peak.",
                  ),
                  l("0.0 – 1.0（步进 0.01）", "0.0 – 1.0 (steps of 0.01)"),
                  l("0.3", "0.3"),
                  l(
                    "录音底噪大、晃动被噪声带得乱跳时调大。",
                    "Raise it when a noisy recording makes the motion jump around.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "这几项只在 7.生成吉他偏移动画 那一步用到；前六步完全不受它们影响，所以可以先不勾、以后再补这一步。",
                "These settings only matter for 7.生成吉他偏移动画 / step 7; the first six steps ignore them entirely, so you can leave the feature off and add step 7 later.",
              ),
            },
          ],
        },
        {
          id: "midi-tracks",
          title: l(
            "03 轨道选择：决定生成哪些音",
            "03 Track Selection — which notes get animated",
          ),
          image: { image: 1 },
          blocks: [
            {
              kind: "p",
              text: l(
                "点过 🔍 扫描MIDI信息 之后，右栏出现的表格就是「这个 MIDI 里有什么」。**勾什么就生成什么**：没勾的轨道不会出现在动画里。",
                "After 🔍 扫描MIDI信息 the table on the right is “what is inside this MIDI”. **What you tick is what gets animated** — unticked tracks never appear in the animation.",
              ),
            },
            {
              kind: "table",
              head: [
                l("列", "Column"),
                l("含义", "Meaning"),
                l("怎么读", "How to read it"),
              ],
              rows: [
                [
                  l("选择", "选择 / Select"),
                  l(
                    "勾选框，决定这一轨要不要生成动画。",
                    "A checkbox deciding whether this track gets animated.",
                  ),
                  l("勾上弹吉他那几轨即可。", "Tick the guitar tracks."),
                ],
                [
                  l("轨道", "轨道 / Track"),
                  l(
                    "MIDI 文件内部的轨道序号。",
                    "The track index inside the MIDI file.",
                  ),
                  l(
                    "这个序号就是生成后 已选择的轨道:、轨道号: 里显示的数字，也是输出文件名里的那一段。",
                    "This is the number shown later in 已选择的轨道: and 轨道号:, and the one embedded in output file names.",
                  ),
                ],
                [
                  l("通道", "通道 / Channel"),
                  l(
                    "这一轨用的 MIDI 通道号；文件里没写通道时显示 N/A。",
                    "The MIDI channel this track uses; N/A when the file does not state one.",
                  ),
                  l(
                    "要按通道过滤时，回上面 通道号: 填这个数字。",
                    "When filtering by channel, copy this number into 通道号: above.",
                  ),
                ],
                [
                  l("乐器", "乐器 / Instrument"),
                  l(
                    "这一轨的乐器名，来自 MIDI 里的乐器号。",
                    "The instrument name for this track, taken from the MIDI program number.",
                  ),
                  l(
                    "用它区分哪一轨是吉他 / 贝斯，哪一轨是鼓。",
                    "Use it to tell guitar and bass tracks from drums.",
                  ),
                ],
                [
                  l("音符数", "音符数 / Notes"),
                  l("这一轨有多少个音符。", "How many notes this track holds."),
                  l(
                    "数字是 0 的轨勾了也没有意义。",
                    "Ticking a track whose count is 0 achieves nothing.",
                  ),
                ],
              ],
            },
            {
              kind: "table",
              head: [
                l("按钮", "Button"),
                l("点了会发生什么", "What happens when you press it"),
              ],
              rows: [
                [
                  l(
                    "✅ 添加选中的轨道",
                    "✅ 添加选中的轨道 / ✅ Add selected tracks",
                  ),
                  l(
                    "把所有勾选的轨道按序号从小到大写进 已选择的轨道:，成功时下方横幅提示 轨道添加成功。一个都没勾就点，只是把列表清空。",
                    "Writes every ticked track into 已选择的轨道: in ascending order and shows the 轨道添加成功 / Tracks added banner. Pressing it with nothing ticked simply clears the list.",
                  ),
                ],
                [
                  l(
                    "🎵 生成优化midi文件",
                    "🎵 生成优化midi文件 / 🎵 Generate optimized MIDI file",
                  ),
                  l(
                    "以当前选中轨道、通道号: 和吉他音域（最低弦的空弦音到最高弦 + 19 品）为界限，删掉音域外的音符，另存一份新 MIDI 文件。成功提示 ✓ 优化文件生成成功: {}，失败提示 ✗ 处理失败: {}；一条轨道都没选时提示 请先选择要处理的轨道。",
                    "Using the selected tracks, 通道号: and the guitar's range (the lowest open string up to the highest string plus 19 frets), it drops out-of-range notes and saves a new MIDI file. Success reads ✓ Optimized file generated: {}; failure reads ✗ Processing failed: {}; with no track selected it says 请先选择要处理的轨道 / Please select the tracks to process first.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "优化完要自己换文件",
                "Reload the optimized file yourself",
              ),
              text: l(
                "🎵 生成优化midi文件 **不会**把当前 MIDI 切成那份新文件。生成完回上面 01 卡片，用 📂 选择MIDI文件 选中新的那份，再 🔍 扫描MIDI信息 重新加一遍轨道。",
                "🎵 生成优化midi文件 does **not** switch the current MIDI to the new file. Go back up to the 01 card, pick the new file with 📂 选择MIDI文件, then 🔍 扫描MIDI信息 and add the tracks again.",
              ),
            },
            {
              kind: "note",
              text: l(
                "还没扫描过任何文件时，右栏是空状态：标题 暂无轨道信息，副标题 请先扫描MIDI文件以查看轨道信息。",
                "Before anything is scanned the right column shows an empty state: 暂无轨道信息 / No track info with the subtitle 请先扫描MIDI文件以查看轨道信息 / Scan a MIDI file first to view track information.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-execute",
      title: l("生成动画", "Generate"),
      image: { image: 3 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一页没有需要调的东西，只有**要按的按钮**和**要看的文字**。左栏上半是这次生成用的参数回显，下半是 操作；右栏整块是 控制台信息。",
            "Nothing on this tab needs tuning — only **buttons to press** and **text to read**. The top-left echoes this run's parameters, the bottom-left holds 操作 / Operations, and the whole right column is 控制台信息 / Console Output.",
          ),
        },
      ],
      subs: [
        {
          id: "execute-echo",
          title: l(
            "左栏回显：先核对再按生成",
            "The echo panel — check before generating",
          ),
          image: { image: 3 },
          blocks: [
            {
              kind: "p",
              text: l(
                "回显区上下两块，上面是角色参数，下面是 MIDI 参数。**它们只是显示，不能在这里改**——每一项都对应前两个页签上的某个控件。",
                "The echo area has two blocks: character parameters on top, MIDI parameters below. **They are display-only** — each row corresponds to a control on one of the previous two tabs.",
              ),
            },
            {
              kind: "table",
              head: [
                l("显示项", "Row"),
                l("显示什么", "What it shows"),
                l("去哪儿改", "Where to change it"),
              ],
              rows: [
                [
                  l("角色: {}", "角色: {} / Character: {}"),
                  l("当前演奏者名字。", "The current performer's name."),
                  l(
                    "角色设置 → Avatar:",
                    "角色设置 / Character Settings → Avatar:",
                  ),
                ],
                [
                  l("使用乐器:", "使用乐器: / Instrument:"),
                  l(
                    "角色的内部乐器名。",
                    "The character's internal instrument name.",
                  ),
                  l(
                    "角色设置 → ✏ 修改 里的 乐器:",
                    "角色设置 / Character Settings → 乐器: / Instrument inside ✏ 修改 / ✏ Edit",
                  ),
                ],
                [
                  l("FPS:", "FPS:"),
                  l("动画帧率。", "The animation frame rate."),
                  l(
                    "角色设置 或 MIDI选择 的 FPS:",
                    "FPS: on 角色设置 / Character Settings or MIDI选择 / MIDI Select",
                  ),
                ],
                [
                  l("吉他弦音高:", "吉他弦音高: / Guitar string pitches:"),
                  l(
                    "整组弦音高，按从细到粗列出。",
                    "All string pitches, thinnest to thickest.",
                  ),
                  l(
                    "角色设置 的 第1弦… 与 常用调弦:",
                    "第1弦… and 常用调弦: on 角色设置 / Character Settings",
                  ),
                ],
                [
                  l("降低八度:", "降低八度: / Octave down:"),
                  l("是 / 否。", "是 / Yes or 否 / No."),
                  l(
                    "角色设置 → 降低八度",
                    "角色设置 / Character Settings → 降低八度 / Octave down",
                  ),
                ],
                [
                  l("变调夹位置:", "变调夹位置: / Capo position:"),
                  l("变调夹品数。", "The capo fret."),
                  l(
                    "角色设置 → 变调夹位置:",
                    "角色设置 / Character Settings → 变调夹位置: / Capo position:",
                  ),
                ],
                [
                  l("使用泛音:", "使用泛音: / Use harmonics:"),
                  l("是 / 否。", "是 / Yes or 否 / No."),
                  l(
                    "角色设置 → 使用泛音",
                    "角色设置 / Character Settings → 使用泛音 / Use harmonics",
                  ),
                ],
                [
                  l("禁用横按:", "禁用横按: / Disable barre:"),
                  l("是 / 否。", "是 / Yes or 否 / No."),
                  l(
                    "角色设置 → 禁用横按",
                    "角色设置 / Character Settings → 禁用横按 / Disable barre",
                  ),
                ],
                [
                  l("MIDI: {}", "MIDI: {}"),
                  l("当前 MIDI 的文件名。", "The current MIDI file name."),
                  l(
                    "MIDI选择 → 📂 选择MIDI文件",
                    "MIDI选择 / MIDI Select → 📂 选择MIDI文件 / Select MIDI File",
                  ),
                ],
                [
                  l("轨道号:", "轨道号: / Track number:"),
                  l(
                    "要生成动画的轨道序号，多个用逗号隔开。",
                    "The tracks to animate, comma-separated.",
                  ),
                  l(
                    "MIDI选择 → ✅ 添加选中的轨道",
                    "MIDI选择 / MIDI Select → ✅ 添加选中的轨道 / Add selected tracks",
                  ),
                ],
                [
                  l("通道号:", "通道号: / Channel number:"),
                  l(
                    "通道过滤值，-1 表示不过滤。",
                    "The channel filter; -1 means no filtering.",
                  ),
                  l(
                    "MIDI选择 → 通道号:",
                    "MIDI选择 / MIDI Select → 通道号: / Channel number:",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              title: l("跑之前扫一眼这四行", "Four rows worth a glance"),
              text: l(
                "角色:、MIDI:、轨道号:、吉他弦音高:。前三个错了动画会跑到别人身上或完全没有音，第四个错了整首曲子的按位都不对。",
                "角色:, MIDI:, 轨道号: / Track number and 吉他弦音高:. Get the first three wrong and the animation plays the wrong performer or nothing at all; get the fourth wrong and every position in the song is off.",
              ),
            },
          ],
        },
        {
          id: "execute-steps",
          title: l(
            "操作：七个按钮和一个一键生成",
            "Operations — seven steps and one-click",
          ),
          image: { image: 3 },
          blocks: [
            {
              kind: "p",
              text: l(
                "操作 里从上到下是七个编号按钮，最后一个 一键生成所有数据 隔在分隔线下面。分步点，每一步的输出会写进 output/ 下的对应文件；一键生成只是把这些步骤按同样的顺序连着做一遍。",
                "操作 / Operations lists seven numbered buttons top to bottom, with 一键生成所有数据 below a separator. Pressed step by step, each one writes its output into the matching file under output/; the one-click button simply runs them in the same order back to back.",
              ),
            },
            {
              kind: "table",
              head: [
                l("按钮", "Button"),
                l("做什么", "What it does"),
                l("什么时候点", "When to press it"),
              ],
              rows: [
                [
                  l("1.初始化", "1.初始化 / 1. Initialize"),
                  l(
                    "读取 MIDI 与角色配置，把音符表、消息表写出来（output/midi_info/ 下那两个文件），并算出全曲的速度与帧数换算。",
                    "Reads the MIDI and the character profile, writes the note map and message map (the two files under output/midi_info/) and works out the song's tempo and frame conversion.",
                  ),
                  l(
                    "换过 MIDI 或角色之后的第一步。",
                    "The first step after changing the MIDI or the performer.",
                  ),
                ],
                [
                  l(
                    "2.生成左手指法",
                    "2.生成左手指法 / 2. Generate left-hand fingering",
                  ),
                  l(
                    "为每个音符时刻搜索最省力的左手按法，写出左手记录；勾了 应用勾弦逻辑 (Pull-off) 时也会在这一步应用。控制台会打印最小消耗熵、预期与实际音符数。",
                    "Searches the cheapest left-hand position for every moment and writes the left-hand records; 应用勾弦逻辑 (Pull-off) is applied here too. The console prints the minimum entropy and the expected versus actual note counts.",
                  ),
                  l("初始化完成后。", "After initialization."),
                ],
                [
                  l(
                    "3.生成右手指法",
                    "3.生成右手指法 / 3. Generate right-hand fingering",
                  ),
                  l(
                    "决定每个音用哪根手指、往哪个方向拨，写出右手记录（output/hand_recorder/ 下 ..._righthand_recorder.json）。这正是 右手编辑器 读写的那份文件。",
                    "Decides which finger plucks each note and in which direction, writing the right-hand records (..._righthand_recorder.json under output/hand_recorder/) — the very file the 右手编辑器 reads and writes.",
                  ),
                  l(
                    "左手指法之后。**注意它会覆盖你在 右手编辑器 里的手工修改。**",
                    "After the left-hand fingering. **It overwrites your hand edits in the 右手编辑器.**",
                  ),
                ],
                [
                  l(
                    "4.生成左手动画",
                    "4.生成左手动画 / 4. Generate left-hand animation",
                  ),
                  l(
                    "把左手记录插值成逐帧动画数据（按弦、抬指、换把、揉弦），写进 output/hand_animation/ 的 ..._lefthand_animation.json。",
                    "Interpolates the left-hand records into per-frame animation (press, release, shifts, vibrato) and writes ..._lefthand_animation.json under output/hand_animation/.",
                  ),
                  l("左手指法之后。", "After the left-hand fingering."),
                ],
                [
                  l(
                    "5.生成右手动画",
                    "5.生成右手动画 / 5. Generate right-hand animation",
                  ),
                  l(
                    "把右手记录插值成逐帧动画数据，写进 ..._righthand_animation.json。在 右手编辑器 改过摇把之后，**必须重跑这一步**改动才会生效。",
                    "Interpolates the right-hand records into per-frame animation and writes ..._righthand_animation.json. After editing whammy values in the 右手编辑器, **re-run this step** for them to take effect.",
                  ),
                  l(
                    "右手指法之后；手工改过右手记录之后。",
                    "After the right-hand fingering, and after hand-editing the right-hand records.",
                  ),
                ],
                [
                  l(
                    "6.生成弦动画",
                    "6.生成弦动画 / 6. Generate string animation",
                  ),
                  l(
                    "生成琴弦动画、活动曲线与摇把 shape key，并**汇总写出最终报告** output/final_result/{角色}_{midi文件名}_{轨道}.json。",
                    "Generates the string animation, activity curve and whammy shape key, and **writes the final report** output/final_result/{performer}_{midi}_{tracks}.json.",
                  ),
                  l(
                    "前五步之后。它是产出报告的那一步。",
                    "After the first five steps — this is the one that produces the report.",
                  ),
                ],
                [
                  l(
                    "7.生成吉他偏移动画",
                    "7.生成吉他偏移动画 / 7. Generate guitar offset animation",
                  ),
                  l(
                    "按 音量偏移参数 里选的音频分析响度，生成整把吉他的位置 / 旋转偏移动画，并重新导出报告。**只有勾了 启用吉他音量偏移动画 这个按钮才会出现。**",
                    "Analyses the audio chosen in 音量偏移参数 and generates the whole guitar's position / rotation offset animation, then re-exports the report. **This button only appears when 启用吉他音量偏移动画 is ticked.**",
                  ),
                  l(
                    "想给整把吉他加晃动时；必须在第 6 步之后。",
                    "When you want the guitar to sway; always after step 6.",
                  ),
                ],
                [
                  l(
                    "一键生成所有数据",
                    "一键生成所有数据 / Generate all data in one go",
                  ),
                  l(
                    "按 1 → 6 的顺序连跑（勾了音量偏移就再加第 7 步），结束时提示 所有数据生成完成。",
                    "Runs steps 1 through 6 back to back (plus step 7 when volume offset is enabled) and finishes with 所有数据生成完成 / All data generation complete.",
                  ),
                  l(
                    "日常就按它。想定位某一步的问题时才分步点。",
                    "Your everyday button. Step through manually only when isolating a problem.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "info",
              title: l(
                "分步点也不会漏初始化",
                "Stepwise still initializes for you",
              ),
              text: l(
                "从 2.生成左手指法 起的每一步，如果发现系统还没初始化，会自己先初始化一次再继续。所以中途换过 MIDI 或角色的话，照样能直接点后面某一步。",
                "From 2.生成左手指法 onward, any step that finds the system uninitialized initializes it first and then continues — so after swapping the MIDI or performer mid-way you can still press a later step directly.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l("参数改了就重跑", "Re-run after changing a parameter"),
              text: l(
                "生成结果不会自动跟着参数更新。改完 角色设置 或 MIDI选择 里的任何一项，回这里重新 一键生成所有数据，否则插件加载到的还是旧数据。",
                "Output does not follow your parameters automatically. After changing anything on 角色设置 / Character Settings or MIDI选择 / MIDI Select, come back and run 一键生成所有数据 again — otherwise the plugin still loads the old data.",
              ),
            },
          ],
        },
        {
          id: "execute-console",
          title: l(
            "控制台信息：怎么判断成功了",
            "Console Output — reading the run",
          ),
          image: { image: 3 },
          blocks: [
            {
              kind: "p",
              text: l(
                "右栏 控制台信息 是这一页真正要盯的地方：进度、警告、错误、最终路径全在这里。",
                "The 控制台信息 / Console Output card on the right is what you actually watch on this tab: progress, warnings, errors and the final path all land here.",
              ),
            },
            {
              kind: "table",
              head: [
                l("按钮", "Button"),
                l("点了会发生什么", "What happens when you press it"),
              ],
              rows: [
                [
                  l("复制输出", "复制输出 / Copy Output"),
                  l(
                    "把控制台里的全部文字复制到剪贴板，便于贴给别人排错。",
                    "Copies every line of the console to the clipboard, handy for pasting into a bug report.",
                  ),
                ],
                [
                  l("复制结果", "复制结果 / Copy Result"),
                  l(
                    "只复制**最终报告的绝对路径**。还没有生成过任何东西时，复制到的是 系统未初始化；生成过但还没产出报告时是 暂无报告文件。",
                    "Copies just the **final report's absolute path**. Before anything has run it copies 系统未初始化 / System not initialized; after a run that produced no report it copies 暂无报告文件 / No report file yet.",
                  ),
                ],
                [
                  l("清空输出", "清空输出 / Clear Output"),
                  l(
                    "清空控制台。已经写出的文件不受影响，只是把文字擦掉。",
                    "Clears the console. Files already written are unaffected — only the text is erased.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "每行的颜色就是它的严重程度：**绿色是成功**（生成左手动画成功: {} 这类）、**红色是错误**（生成左手指法失败: {} 这类）、**黄色是警告**、白色是普通信息。有新的输出时控制台会自动滚到底部；你手动往上翻时它就不再抢着滚，翻回底部后恢复跟随。",
                "Line colour carries the severity: **green is success** (things like Left-hand animation generated: {}), **red is an error** (things like Left-hand fingering generation failed: {}), **yellow is a warning** and white is ordinary information. The console auto-scrolls to the bottom as output arrives; once you scroll up it stops chasing you and resumes when you return to the bottom.",
              ),
            },
            {
              kind: "p",
              text: l(
                "一轮跑完，最后通常能看到 报告已保存至: 加路径、以及结尾的 所有数据生成完成；中间还会列出左右手记录、动画文件、琴弦动画文件各自保存到了哪里。",
                "A finished run normally ends with 报告已保存至: plus the path and the closing 所有数据生成完成. Along the way it also lists where the left-hand and right-hand records, the animation files and the string animation file were saved.",
              ),
            },
            {
              kind: "note",
              text: l(
                "看到 生成过程中碰到左手无法按弦的音符组合： 不用紧张：它只是告诉你某些音超出了手能按到的范围，程序已经跳过这些音继续往下做，并不等于整次生成失败。",
                "Do not panic at 生成过程中碰到左手无法按弦的音符组合： / Encountered note combinations the left hand cannot press: it only means some notes fall outside what a hand can reach. The run skips them and continues — it is not a failure.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tab-recorder",
      title: l("右手编辑器", "Recorder Editor"),
      image: { image: 4 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一页签里其实是**两个视图**，用窗口左上角那两个标签切换：🎸 指法时间轴 和 ✏ 右手编辑。同一时刻只显示一个。它们读取的都是前面生成出来的文件，**本身不参与生成**。",
            "This tab actually holds **two views**, switched by the two labels at the top left: 🎸 指法时间轴 / Fingering timeline and ✏ 右手编辑 / Right-hand edit. Only one shows at a time. Both read files produced by the generation steps and **take no part in generating**.",
          ),
        },
        {
          kind: "note",
          text: l(
            "页面默认打开的是 🎸 指法时间轴。加载用的路径都从 MIDI选择 页当前的 MIDI 与轨道推算，所以**先在那一页选好文件与轨道，再来这一页**，否则两个视图都会提示你先去设置。",
            "The 🎸 指法时间轴 view is what opens by default. Both views derive their file paths from the MIDI and tracks currently set on the MIDI选择 tab, so **set those first** — otherwise both views tell you to go and do it.",
          ),
        },
      ],
      subs: [
        {
          id: "recorder-timeline",
          title: l(
            "🎸 指法时间轴：看左手按了什么",
            "🎸 Fingering timeline — what the left hand does",
          ),
          image: { image: 2 },
          blocks: [
            {
              kind: "p",
              text: l(
                "卡片标题 左手指法时间轴，副标题把画法说得很清楚：每根弦一条线，横轴为时间，线上数字为该弦要按的品格（0 = 空弦）。它把左手记录和右手触弦记录合到一起，**把不会发音的弦过滤掉**，所以你看到的是「真正会响的音」。",
                "The card is titled 左手指法时间轴 / Left-hand fingering timeline, and its subtitle states the drawing rule plainly: 每根弦一条线，横轴为时间，线上数字为该弦要按的品格（0 = 空弦） / One line per string; the horizontal axis is time and the number is the fret to press on that string (0 = open string). It merges the left-hand records with the right-hand touch records and **drops strings that never sound**, so what you see is what will actually be heard.",
              ),
            },
            {
              kind: "p",
              text: l(
                "如果这份指法文件还不存在，卡片会先给你一条横幅：还没有生成指法时间轴文件。点下面的「生成」按钮：它会结合右手触弦记录，把不会发音的弦过滤掉。，下面是 文件: {} 与按钮 ⚙ 生成指法时间轴。没有选定 MIDI 时按钮不可用，并提示 请先在「MIDI选择」里选定 MIDI 文件与轨道。生成成功后页面直接变成谱面视图。",
                "If the fingering file does not exist yet, the card first shows the banner 还没有生成指法时间轴文件。点下面的「生成」按钮：它会结合右手触弦记录，把不会发音的弦过滤掉。, then File: {} and the ⚙ 生成指法时间轴 / Generate fret timeline button. Without a selected MIDI the button is disabled and the card says 请先在「MIDI选择」里选定 MIDI 文件与轨道. Once generation succeeds the page turns into the chart view.",
              ),
            },
            {
              kind: "p",
              text: l(
                "已经有文件时，卡片从上到下是这样的：",
                "With a file in place the card reads top to bottom like this:",
              ),
            },
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("作用", "What it does"),
                l("怎么用", "How to use it"),
              ],
              rows: [
                [
                  l(
                    "文件: {} + ⚙ 重新生成",
                    "文件: {} + ⚙ 重新生成 / ⚙ Regenerate",
                  ),
                  l(
                    "显示指法文件路径；右侧按钮重新生成一次并覆盖同名文件。",
                    "Shows the fingering file path; the button on the right regenerates it, overwriting the same file.",
                  ),
                  l(
                    "刚重跑过左手数据、但还没有指法文件时点它；没选定 MIDI 时按钮不可用。",
                    "Press it after re-running the left-hand data when the fingering file is not there yet; disabled without a selected MIDI.",
                  ),
                ],
                [
                  l(
                    "音符 {} 个 · {} 根弦 · 时长约 {} 秒",
                    "{} notes · {} strings · about {} s",
                  ),
                  l(
                    "统计行：音符数、弦数（按当前调弦）、整段时长。",
                    "A statistics line: note count, string count (from the current tuning) and the total length.",
                  ),
                  l(
                    "核对用。旁边偶尔出现 （{} 条无法识别的状态已跳过），表示有少数记录格式不认识、已忽略。",
                    "For checking. It occasionally adds ({} entries with unrecognized state skipped), meaning a few records had an unrecognized state and were skipped.",
                  ),
                ],
                [
                  l("进度条", "Progress bar"),
                  l(
                    "整段曲子的密度图，并框出当前视窗的位置。",
                    "A density map of the whole song with the current view window boxed on it.",
                  ),
                  l(
                    "拖动它平移视窗；**没在播放时**拖动还会把播放头挪到视窗中心，省得你拖完再找位置。",
                    "Drag it to pan the view. While **not** playing, dragging also moves the playhead to the centre of the window, so you do not have to hunt for the spot again.",
                  ),
                ],
                [
                  l(
                    "视窗 {}s – {}s / 已放大 {}x / 复位",
                    "View {}s – {}s / zoomed {}x / Reset",
                  ),
                  l(
                    "当前看到的时间段与放大倍数。",
                    "The time span currently on screen and the zoom factor.",
                  ),
                  l(
                    "点 复位 回到整段；没放大过时 复位 不显示。",
                    "Press 复位 to return to the whole song; it is hidden while you are not zoomed in.",
                  ),
                ],
                [
                  l("▶ 播放 / ⏸ 暂停", "▶ 播放 / ⏸ 暂停 / ▶ Play / ⏸ Pause"),
                  l(
                    "用系统 MIDI 输出把你选的 MIDI 放出来，红色播放头跟着走。",
                    "Plays your MIDI through the system MIDI output with the red playhead following along.",
                  ),
                  l(
                    "没选定 MIDI 时不可用，并显示 未选择 MIDI 文件，无法播放。",
                    "Unavailable without a selected MIDI, where it reads 未选择 MIDI 文件，无法播放 / No MIDI file selected; cannot play.",
                  ),
                ],
                [
                  l("⏹ 停止", "⏹ 停止 / ⏹ Stop"),
                  l(
                    "停止播放并回到起点。",
                    "Stops playback and returns to the start.",
                  ),
                  l("—", "—"),
                ],
                [
                  l("◀ / ▶", "◀ / ▶"),
                  l(
                    "单帧步进：按动画帧前后挪一格。",
                    "Single-frame stepping: moves one animation frame back or forward.",
                  ),
                  l(
                    "想逐帧看清某个按弦动作时用。",
                    "Use it to inspect one press frame by frame.",
                  ),
                ],
                [
                  l("⏱ {}s / {}s", "⏱ {}s / {}s"),
                  l(
                    "播放头当前位置与整段总时长，单位秒。",
                    "The playhead position and total length, in seconds.",
                  ),
                  l("—", "—"),
                ],
                [
                  l("MIDI 输出 ({}):", "MIDI out ({}):"),
                  l(
                    "选择播放用的 MIDI 设备。默认是 自动（程序自己挑一个，选中时会把设备名写在括号里），也可以在下拉里指定某一个。",
                    "Chooses which MIDI device to play through. The default is 自动 / Auto — the app picks one and shows its name in brackets — but you can name a specific device in the dropdown.",
                  ),
                  l(
                    "右侧会显示当前状态：能出声时是绿色的设备名，不可用时是红色的失败原因，还没启动时是 MIDI: 未启动。",
                    "The status appears to the right: the device name in green when it works, the failure reason in red when it does not, and MIDI: 未启动 before it starts.",
                  ),
                ],
                [
                  l("🔔 测试音", "🔔 测试音 / 🔔 Test tone"),
                  l(
                    "往当前设备发一个测试音。",
                    "Sends a test tone to the current device.",
                  ),
                  l(
                    "播放没声音时先用它确认设备通不通。",
                    "Use it first when playback is silent, to confirm the device works.",
                  ),
                ],
                [
                  l("谱面上的数字", "The numbers on the chart"),
                  l(
                    "每根弦一条横线，数字徽标就是要按的品格。徽标按来源分色并标注类型：按弦、空弦、横按。",
                    "One horizontal line per string; each numbered badge is the fret to press. Badges are colour-coded by origin and labelled 按弦 / pressed, 空弦 / open string or 横按 / barre.",
                  ),
                  l(
                    "点任意一个数字，下面显示该音符详情：时间 {}s · frame {} · tick {} · {} 弦 · {} 品 · 手指 {} · {}；什么都没选时显示 点击琴弦上的任意数字，查看该音符详情。",
                    "Click any number to show its details below: 时间 {}s · frame {} · tick {} · {} 弦 · {} 品 · 手指 {} · {}. With nothing selected it reads 点击琴弦上的任意数字，查看该音符详情。 / Click any number on a string to see its details.",
                  ),
                ],
                [
                  l("Ctrl + 滚轮", "Ctrl + scroll"),
                  l(
                    "在谱面上横向缩放，以光标所在的时间为锚点。",
                    "Zooms horizontally on the chart, anchored at the time under the cursor.",
                  ),
                  l(
                    "缩放范围：最小 20 帧，最大整段的 4 倍。用普通滚轮不行——那是页面滚动。",
                    "The zoom range runs from 20 frames at the tightest to four times the whole song. Plain scrolling will not do it — that scrolls the page.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "info",
              title: l(
                "听到的和看到的是两套东西",
                "Hearing and seeing are two separate things",
              ),
              text: l(
                "播放的声音来自 MIDI 文件本身（走系统合成器），和生成的动画数据无关；播放头位置也只是按帧换算出来的参考。它的用途是让你**对着声音检查按位**。",
                "The sound comes from the MIDI file itself through the system synth and has nothing to do with the generated animation data; the playhead is only a frame-derived reference. Its purpose is to let you **check positions against what you hear**.",
              ),
            },
            {
              kind: "note",
              text: l(
                "指法文件的路径是 output/hand_recorder/{midi文件名}_{轨道}_fret_timeline.json。如果这一份是从旧格式的左手记录反推出来的，卡片里会出现一条黄色提示：这份指法是从旧格式的左手 recorder 反推出来的（横按覆盖的弦可能不准）…——重新生成一次左手记录并按 ⚙ 重新生成 就会消失。",
                "The fingering file lives at output/hand_recorder/{midi}_{tracks}_fret_timeline.json. If it was reverse-engineered from a legacy left-hand recorder, a yellow note appears: 这份指法是从旧格式的左手 recorder 反推出来的（横按覆盖的弦可能不准）… / This fingering was reverse-engineered from a legacy left-hand recorder. Regenerate the left-hand records and press ⚙ 重新生成 and it goes away.",
              ),
            },
          ],
        },
        {
          id: "recorder-edit",
          title: l(
            "✏ 右手编辑：手工修摇把",
            "✏ Right-hand edit — fixing whammy by hand",
          ),
          image: { image: 4 },
          blocks: [
            {
              kind: "p",
              text: l(
                "卡片标题 右手 Recorder 编辑器，副标题 电吉他右手拨弦记录浏览与编辑。**这一屏是给电吉他用的**：指弹和贝斯没有摇把，一般不需要过来。它读写的是右手记录文件本身，改完要回 生成动画 重跑第 5 步才会进动画。",
                "The card is 右手 Recorder 编辑器 / Right-Hand Recorder Editor with the subtitle 电吉他右手拨弦记录浏览与编辑 / Browse and edit electric guitar right-hand plucking records. **This screen is for electric guitar** — fingerstyle and bass have no whammy and rarely need it. It reads and writes the right-hand record file itself, so you must re-run step 5 on the 生成动画 / Generate tab for edits to reach the animation.",
              ),
            },
            {
              kind: "p",
              text: l(
                "顶部两行是文件信息：文件: 给出完整路径（还没有路径时显示 未加载），记录数: 形如 {} 条；一旦你改过任何一项，旁边会出现 ⚠ 有未保存的修改。",
                "Two lines at the top carry file information: File: gives the full path (or 未加载 / Not loaded when there is none) and Records: reads {} records. As soon as you change anything, ⚠ 有未保存的修改 / ⚠ Unsaved changes appears next to it.",
              ),
            },
            {
              kind: "p",
              text: l(
                "文件读不出来时整页只剩一条横幅：有路径但读取或解析失败 → 无法加载右手记录文件，请确认 MIDI 文件已执行过生成；连路径都没有 → 请先在 MIDI 选择面板设置 MIDI 文件和轨道，再切换到本面板。",
                "When the file cannot be read the page shows a single banner: with a path that fails to read or parse, 无法加载右手记录文件，请确认 MIDI 文件已执行过生成 / Failed to load the right-hand recorder file; with no path at all, 请先在 MIDI 选择面板设置 MIDI 文件和轨道，再切换到本面板 / Set the MIDI file and tracks in the MIDI Select panel first.",
              ),
            },
            {
              kind: "table",
              head: [
                l("控件", "Control"),
                l("作用", "What it does"),
                l("说明", "Notes"),
              ],
              rows: [
                [
                  l("帧数:", "帧数: / Frame:"),
                  l(
                    "要找的动画帧号。",
                    "The animation frame you are looking for.",
                  ),
                  l(
                    "占位提示 留空=全局 —— 留空就是不按帧号筛，列出全部记录。",
                    "Placeholder 留空=全局 / empty = all — leaving it empty lists every record.",
                  ),
                ],
                [
                  l("± 帧数", "± frame range"),
                  l(
                    "以 帧数: 为中心的前后搜索半径。",
                    "The search radius around 帧数: / Frame.",
                  ),
                  l(
                    "0 – 200 帧，默认 10。填 0 就只匹配那一帧。",
                    "0 – 200 帧 / f, default 10. Zero matches that frame only.",
                  ),
                ],
                [
                  l("🔍 搜索", "🔍 搜索 / 🔍 Search"),
                  l(
                    "按 帧数: 与 插入摇把: 两个条件筛选。",
                    "Filters by 帧数: / Frame and 插入摇把: / Insert whammy together.",
                  ),
                  l(
                    "结果数量显示在下面的 匹配结果: 一行。",
                    "The number of hits shows on the 匹配结果: / Matches line below.",
                  ),
                ],
                [
                  l("🔄 刷新", "🔄 刷新 / 🔄 Refresh"),
                  l(
                    "丢掉内存里的改动，重新从文件读一遍。",
                    "Discards the in-memory copy and reads the file again.",
                  ),
                  l(
                    "外部改过文件、或者想从头再来时用。",
                    "Use it after editing the file elsewhere, or to start over.",
                  ),
                ],
                [
                  l("✕ 清除", "✕ 清除 / ✕ Clear"),
                  l(
                    "清空 帧数:、清空结果、插入摇把: 回到 任意。",
                    "Empties 帧数: / Frame, clears the results and resets 插入摇把: / Insert whammy to 任意 / Any.",
                  ),
                  l(
                    "只是清搜索条件，不动数据。",
                    "It only resets the search — no data is touched.",
                  ),
                ],
                [
                  l("插入摇把:", "插入摇把: / Insert whammy:"),
                  l(
                    "按「这一帧有没有插摇把」过滤。",
                    "Filters by whether a whammy is inserted on that frame.",
                  ),
                  l(
                    "三个选项：任意 / 是 / 否，默认 任意。",
                    "Three choices: 任意 / Any, 是 / Yes, 否 / No — default 任意.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "结果以 匹配结果: {} 条 开头，每行 3 张卡片、**最多显示前 100 张**。一条都没有时显示 暂无匹配结果，并附上两种提示之一：还没输入任何条件时是 共 {} 条记录，输入帧数或选择摇把筛选条件进行搜索，条件没匹配上时是 没有找到匹配的记录，请调整搜索条件。",
                "Results start with Matches: {} records and lay out three cards per row, **capped at the first 100**. With none you get 暂无匹配结果 / No results plus one of two hints: {} records in total. Enter a frame number or pick a whammy filter to search while no criteria are set, or 没有找到匹配的记录，请调整搜索条件 / No matching records. Please adjust your search criteria when they matched nothing.",
              ),
            },
            {
              kind: "table",
              head: [
                l("卡片字段", "Card field"),
                l("含义", "Meaning"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("能改吗", "Editable"),
              ],
              rows: [
                [
                  l("帧 {}", "Frame {}"),
                  l(
                    "这条记录属于哪个动画帧；整数帧显示整数，非整数帧保留两位小数。",
                    "Which animation frame the record belongs to; whole frames print as integers, others with two decimals.",
                  ),
                  l("—", "—"),
                  l("文件里的值", "The value in the file"),
                  l("否", "No"),
                ],
                [
                  l("#{index}", "#{index}"),
                  l(
                    "这条记录在文件里的下标。",
                    "The record's index in the file.",
                  ),
                  l("—", "—"),
                  l("—", "—"),
                  l("否", "No"),
                ],
                [
                  l("strings: []", "strings: []"),
                  l(
                    "这一帧要拨的弦序号。空的 [] 表示这一帧没有触弦记录。",
                    "Which strings are plucked on this frame. An empty [] means no touch record here.",
                  ),
                  l("—", "—"),
                  l("文件里的值", "The value in the file"),
                  l("否", "No"),
                ],
                [
                  l("→下一帧差: {} 帧", "→Next frame diff: {} f"),
                  l(
                    "与下一条记录相差多少帧。它是算 周期数: 的输入；大于 0 时用主题色高亮。",
                    "How many frames separate this record from the next. It feeds 周期数: / Cycles and is highlighted when greater than 0.",
                  ),
                  l("—", "—"),
                  l("文件里的值", "The value in the file"),
                  l("否", "No"),
                ],
                [
                  l("插入摇把", "插入摇把 / Insert whammy"),
                  l(
                    "这一帧要不要插摇把动作。",
                    "Whether a whammy motion is inserted on this frame.",
                  ),
                  l("勾选 / 不勾", "Ticked / unticked"),
                  l("文件里的值", "The value in the file"),
                  l("是", "Yes"),
                ],
                [
                  l("半周期(s):", "半周期(s): / Half period (s):"),
                  l(
                    "摇把往复的半周期时长：越小抖得越快。",
                    "The half period of the whammy oscillation: smaller means faster wobble.",
                  ),
                  l("0.05 – 2.00", "0.05 – 2.00"),
                  l("0.35", "0.35"),
                  l("是", "Yes"),
                ],
                [
                  l("占比:", "占比: / Usage:"),
                  l(
                    "摇把占这一段时长的比例，越大动作越「满」。",
                    "The share of the segment the whammy occupies; higher means a fuller motion.",
                  ),
                  l("0.10 – 1.00", "0.10 – 1.00"),
                  l("1.00", "1.00"),
                  l("是", "Yes"),
                ],
                [
                  l("周期数:", "周期数: / Cycles:"),
                  l(
                    "由 →下一帧差、FPS 和上面两个参数实时算出的往复次数，只读；大于 0 时用绿色显示。改上面任意一项都会立刻重算。",
                    "The oscillation count derived live from →下一帧差, FPS and the two parameters above. Read-only, shown in green when greater than 0, and recalculated the moment you change either parameter.",
                  ),
                  l("—", "—"),
                  l("自动算出", "Computed"),
                  l("否", "No"),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "只要动过任何一张卡片，匹配结果: 那一行的右侧就出现两个按钮：💾 保存到文件 把**整份记录**以美化 JSON 写回原路径；✕ 取消修改 丢弃全部改动、回到上次保存的状态。没有未保存修改时这两个按钮不显示。",
                "As soon as you touch any card, two buttons appear at the right of the 匹配结果: / Matches row: 💾 保存到文件 / Save to file writes the **whole record set** back to the same path as pretty JSON, and ✕ 取消修改 / Discard changes throws away every edit and returns to the last saved state. Neither shows while there is nothing unsaved.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l("保存之后必须重跑第 5 步", "Re-run step 5 after saving"),
              text: l(
                "这里保存的只是右手记录，动画文件不会自动更新——回 生成动画 点一次 5.生成右手动画，改动才会体现到动画数据里。反过来也要小心：3.生成右手指法 和 一键生成所有数据 会重新生成右手记录，**把你的手工修改覆盖掉**。",
                "Saving here only updates the right-hand records — the animation file is not refreshed. Go back to 生成动画 / Generate and press 5.生成右手动画 once for the edits to reach the animation data. Be careful the other way round too: 3.生成右手指法 and 一键生成所有数据 regenerate those records and **overwrite your hand edits**.",
              ),
            },
            {
              kind: "callout",
              tone: "tip",
              title: l("典型用法", "A typical session"),
              text: l(
                "动画里某一小节的摇把节奏不对：帧数: 填那一帧、± 给个范围 → 🔍 搜索 定位 → 逐张卡片调 半周期(s): 与 占比:，盯着 周期数: 的变化 → 💾 保存到文件 → 回 生成动画 重跑 5.生成右手动画。",
                "One bar's whammy rhythm looks wrong: enter that frame in 帧数: / Frame with a small ± range → locate it with 🔍 搜索 / Search → tune 半周期(s): and 占比: card by card while watching 周期数: / Cycles → 💾 保存到文件 / Save to file → back on 生成动画 / Generate, re-run 5.生成右手动画.",
              ),
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "defaults",
      title: l("可以完全不动的那一栏", "The column you can leave alone"),
      blocks: [
        {
          kind: "p",
          text: l(
            "把所有可调参数按「全新安装时的默认值」列在一起，方便对照。**右列写的是「什么时候才需要动」**——没遇上那种情况，就让它保持默认。",
            "Every adjustable parameter with its fresh-install default, for reference. **The right column says when you would actually touch it** — if that situation has not come up, leave it alone.",
          ),
        },
        {
          kind: "table",
          head: [
            l("参数", "Parameter"),
            l("默认", "Default"),
            l("什么时候才需要动", "When you would change it"),
          ],
          rows: [
            [
              l("Avatar:", "Avatar:"),
              l("Miku", "Miku"),
              l(
                "换演奏者、换乐器类型。",
                "Switching performer or instrument type.",
              ),
            ],
            [
              l("FPS:", "FPS:"),
              l("60", "60"),
              l(
                "目标软件的帧率和 60 不同。",
                "When your 3D app does not run at 60.",
              ),
            ],
            [
              l("常用调弦:", "常用调弦: / Common tunings:"),
              l(
                "显示 选择预设调弦",
                "Shows 选择预设调弦 / Select preset tuning",
              ),
              l(
                "曲子用特殊调弦或换了乐器。",
                "The song uses a special tuning, or you switched instruments.",
              ),
            ],
            [
              l("第1弦 … 第6弦", "第1弦 … 第6弦 / String 1 … String 6"),
              l("标准吉他 e b G D A E1", "Standard guitar: e b G D A E1"),
              l(
                "预设不匹配你的乐器时。",
                "When no preset matches your instrument.",
              ),
            ],
            [
              l("降低八度", "降低八度 / Octave down"),
              l("不勾", "Unticked"),
              l(
                "生成的按位整体高了一个八度。",
                "When every position comes out an octave too high.",
              ),
            ],
            [
              l("变调夹位置:", "变调夹位置: / Capo position:"),
              l("0", "0"),
              l("曲子是夹着变调夹弹的。", "The song is played with a capo."),
            ],
            [
              l("使用泛音", "使用泛音 / Use harmonics"),
              l("不勾", "Unticked"),
              l("曲子里有泛音。", "The song has harmonics."),
            ],
            [
              l("禁用横按", "禁用横按 / Disable barre"),
              l("不勾", "Unticked"),
              l(
                "横按太别扭，或角色手型压不住。",
                "Barres look awkward, or the character's hand cannot hold one.",
              ),
            ],
            [
              l(
                "应用勾弦逻辑 (Pull-off)",
                "应用勾弦逻辑 (Pull-off) / Apply pull-off logic",
              ),
              l("不勾", "Unticked"),
              l(
                "想要更自然的连音；反过来出现漏音时先关掉。",
                "For more natural legato — or switch it off first when notes go missing.",
              ),
            ],
            [
              l(
                "按弦时间: / 抬指时间: / 移动速度:",
                "按弦时间: / 抬指时间: / 移动速度: / Press time, Release time, Move speed",
              ),
              l("各 0.100 秒", "0.100 s each"),
              l(
                "动作赶不上快曲子，或看起来太僵硬。",
                "The hands lag behind fast passages or look stiff.",
              ),
            ],
            [
              l(
                "揉弦速度: / 揉弦幅度:",
                "揉弦速度: / 揉弦幅度: / Vibrato speed, Vibrato amplitude",
              ),
              l("各 1.00 倍", "1.00x each"),
              l(
                "揉弦看着太夸张或几乎看不出来。",
                "Vibrato looks too wide or barely visible.",
              ),
            ],
            [
              l("通道号:", "通道号: / Channel:"),
              l("-1", "-1"),
              l(
                "一个文件里有多个声部、只想取一路。",
                "One file holds several parts and you want only one.",
              ),
            ],
            [
              l("音符量化:", "音符量化: / Note quantization:"),
              l("1/64", "1/64"),
              l(
                "节奏对不齐，或不想让音符被挪动。",
                "The rhythm sounds misaligned, or you want notes left untouched.",
              ),
            ],
            [
              l("已选择的轨道:", "已选择的轨道: / Selected tracks:"),
              l("轨道 1", "轨道 1 / Track 1"),
              l("换文件之后必须重选。", "Always after changing the file."),
            ],
            [
              l(
                "启用吉他音量偏移动画",
                "启用吉他音量偏移动画 / Enable guitar volume offset animation",
              ),
              l("不勾", "Unticked"),
              l(
                "想让整把吉他跟着响度晃。",
                "When you want the whole guitar to sway with loudness.",
              ),
            ],
            [
              l(
                "最大位置偏移: / 最大旋转偏移(°):",
                "最大位置偏移: / 最大旋转偏移(°): / Max position offset, Max rotation offset",
              ),
              l("10 / 10", "10 / 10"),
              l("晃动幅度太大或太小。", "The sway is too large or too small."),
            ],
            [
              l(
                "检测灵敏度: / 噪声过滤比例:",
                "检测灵敏度: / 噪声过滤比例: / Detection sensitivity, Noise filter ratio",
              ),
              l("1.5 / 0.3", "1.5 / 0.3"),
              l(
                "晃动太碎，或底噪把晃动带得乱跳。",
                "The motion is jittery, or noise makes it jump around.",
              ),
            ],
            [
              l(
                "半周期(s): / 占比:",
                "半周期(s): / 占比: / Half period (s), Usage",
              ),
              l("0.35 / 1.00", "0.35 / 1.00"),
              l(
                "某几帧的摇把节奏不对时手工改。",
                "Hand-fixing the whammy rhythm on specific frames.",
              ),
            ],
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "faq",
      title: l("参数相关的常见疑问", "Common parameter questions"),
      blocks: [
        {
          kind: "callout",
          tone: "info",
          title: l(
            "改了参数，要重跑哪几步？",
            "Which steps must I re-run after a change?",
          ),
          text: l(
            "最稳的做法是回 生成动画 重新 一键生成所有数据。按依赖看：角色设置 里的调弦、变调夹、降低八度、泛音、横按、勾弦、时间线、揉弦与 FPS 都影响指法或动画，所以从 2.生成左手指法 起都要重跑；只有 音量偏移参数 是例外，它只影响第 7 步。",
            "The safe answer is to go back to 生成动画 / Generate and run 一键生成所有数据 / Generate all data in one go again. Following the dependencies: tuning, capo, octave down, harmonics, barre, pull-off, timeline settings, vibrato and FPS on the 角色设置 / Character Settings tab all affect fingering or animation, so everything from 2.生成左手指法 onward must be re-run. Only the 音量偏移参数 group is the exception — it only affects step 7.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "为什么 人物信息 里显示 finger_style_guitar 而不是中文？",
            "Why does 人物信息 / Character Info show finger_style_guitar instead of a translated name?",
          ),
          text: l(
            "那一行显示的是角色登记文件里的**内部乐器名**（finger_style_guitar / bass / electric_guitar），程序直接照抄。中文名（指弹吉他 / 贝斯 / 电吉他）只出现在 ➕ 新增 / ✏ 修改 对话框的 乐器: 下拉里。",
            "That line prints the **internal instrument name** from the character registry — finger_style_guitar, bass or electric_guitar — exactly as stored. The localized names (指弹吉他 / Fingerstyle Guitar, 贝斯 / Bass, 电吉他 / Electric Guitar) appear only in the 乐器: / Instrument dropdown of the ➕ 新增 / Add and ✏ 修改 / Edit dialog.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "揉弦幅度: 调了没反应？",
            "揉弦幅度: / Vibrato amplitude seems to do nothing",
          ),
          text: l(
            "它只对指弹 / 贝斯的平移揉弦生效；电吉他的旋转幅度固定 5°，不随这个倍率变化。界面上的悬停提示也是这么写的。",
            "It applies to fingerstyle and bass translation vibrato only; electric-guitar rotation amplitude is fixed at 5° and ignores the multiplier. The tooltip in the app says the same thing.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "检测灵敏度: 该调大还是调小？",
            "Should 检测灵敏度: / Detection sensitivity go up or down?",
          ),
          text: l(
            "它是阈值倍数：**越大，检测到的响度点越少**，吉他晃得越少。晃动太密集就调大，整首歌几乎不动就调小。",
            "It is a threshold multiplier: **higher means fewer loudness peaks detected** and less sway. Raise it when the motion is too dense, lower it when the guitar hardly moves.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "点了一键生成，为什么没有第 7 步？",
            "Why is there no step 7 after Generate all?",
          ),
          text: l(
            "第 7 步只在你勾了 启用吉他音量偏移动画 时才存在。先去 MIDI选择 勾上它、选好音频，操作 里才会出现 7.生成吉他偏移动画，一键生成也才会带上它。",
            "Step 7 only exists when 启用吉他音量偏移动画 is ticked. Tick it on the MIDI选择 / MIDI Select tab and choose the audio, and 操作 / Operations gains 7.生成吉他偏移动画 — the one-click run then includes it too.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          title: l(
            "生成完能删掉 output/ 里的中间文件吗？",
            "Can I delete the intermediate files under output/?",
          ),
          text: l(
            "要小心：最终报告 JSON 里存的是那些数据文件的**绝对路径**，被它引用到的文件（左右手动画、弦动画、活动曲线、摇把 shape key，启用偏移时还有吉他偏移动画）都必须留着。左手 / 右手 recorder 和 midi_info 里的文件插件不读，只是排查时有用。",
            "Careful: the final report stores **absolute paths** to the data files, and everything it references — left-hand and right-hand animation, string animation, activity curve, whammy shape key, plus the guitar offset animation when enabled — must stay. The left-hand and right-hand recorders and the files under midi_info are not read by the plugin; they are only useful for troubleshooting.",
          ),
        },
      ],
    },
  ],
};
